import { useState } from 'react';
import { Plus, Trash2, MessageSquare, Check, X } from 'lucide-react';
import { cn } from '../ui';
import type { ConversationSummary } from './use-assistant-chat';

/**
 * Conversations panel for the /agente workspace — the secondary rail that lists
 * every thread and lets the officer start / switch / delete conversations. It is
 * presentational: the provider owns the data and the mutations; the page decides
 * where it renders (a permanent rail on lg+, a slide-over below lg).
 *
 * Threads are grouped by recency (Hoy / Ayer / Anteriores) with an es-PA relative
 * time, the active one highlighted. Delete is a hover trash with a light inline
 * confirm (no blocking dialog), so a mis-click never silently drops a thread.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

/** Midnight (local) of the day `ts` falls on — for calendar-day bucketing. */
function startOfDay(ts: number): number {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

type Bucket = 'hoy' | 'ayer' | 'anteriores';

const BUCKET_LABEL: Record<Bucket, string> = {
  hoy: 'Hoy',
  ayer: 'Ayer',
  anteriores: 'Anteriores',
};

function bucketOf(ts: number, now: number): Bucket {
  const today = startOfDay(now);
  const day = startOfDay(ts);
  if (day >= today) return 'hoy';
  if (day >= today - DAY_MS) return 'ayer';
  return 'anteriores';
}

const dateFmt = new Intl.DateTimeFormat('es-PA', { day: 'numeric', month: 'short' });

/** Short es-PA relative time: "ahora", "hace 5 min", "hace 3 h", "ayer", "10 jul". */
function relativeTime(ts: number, now: number): string {
  const diff = now - ts;
  if (diff < 60_000) return 'ahora';
  if (diff < 3_600_000) return `hace ${Math.floor(diff / 60_000)} min`;
  const bucket = bucketOf(ts, now);
  if (bucket === 'hoy') return `hace ${Math.floor(diff / 3_600_000)} h`;
  if (bucket === 'ayer') return 'ayer';
  return dateFmt.format(ts);
}

export function ConversationList({
  conversations,
  activeId,
  onSelect,
  onNew,
  onDelete,
}: {
  conversations: ConversationSummary[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  onDelete: (id: string) => void;
}) {
  // Which row is showing its inline "¿Eliminar?" confirm. Reset whenever a delete
  // resolves or the user acts elsewhere.
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const now = Date.now();

  // Sort newest-activity first, then split into recency buckets in display order.
  const sorted = [...conversations].sort((a, b) => b.updatedAt - a.updatedAt);
  const groups: Array<{ bucket: Bucket; items: ConversationSummary[] }> = [];
  for (const bucket of ['hoy', 'ayer', 'anteriores'] as const) {
    const items = sorted.filter((c) => bucketOf(c.updatedAt, now) === bucket);
    if (items.length > 0) groups.push({ bucket, items });
  }

  return (
    <div className="flex h-full flex-col">
      <button
        type="button"
        onClick={() => {
          setConfirmId(null);
          onNew();
        }}
        className="mb-3 flex w-full items-center justify-center gap-2 rounded-xl border border-primary/25 bg-primary/[0.06] px-3 py-2.5 text-sm font-semibold text-primary-dark transition-colors hover:border-primary hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      >
        <Plus className="h-4 w-4" strokeWidth={2.25} />
        Nueva conversación
      </button>

      <div className="-mr-1 min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
        {groups.map(({ bucket, items }) => (
          <div key={bucket}>
            <div className="px-1 pb-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-ink-faint">
              {BUCKET_LABEL[bucket]}
            </div>
            <ul className="space-y-1">
              {items.map((c) => {
                const active = c.id === activeId;
                const confirming = c.id === confirmId;
                return (
                  <li key={c.id} className="group relative">
                    <button
                      type="button"
                      onClick={() => {
                        setConfirmId(null);
                        onSelect(c.id);
                      }}
                      aria-current={active ? 'true' : undefined}
                      className={cn(
                        'flex w-full items-start gap-2.5 rounded-xl px-3 py-2 pr-9 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
                        active ? 'bg-primary/10' : 'hover:bg-panel'
                      )}
                    >
                      <MessageSquare
                        className={cn(
                          'mt-0.5 h-4 w-4 shrink-0',
                          active ? 'text-primary-dark' : 'text-ink-faint'
                        )}
                        strokeWidth={1.75}
                      />
                      <span className="min-w-0 flex-1">
                        <span
                          className={cn(
                            'block truncate text-sm',
                            active ? 'font-semibold text-ink' : 'font-medium text-ink-soft'
                          )}
                        >
                          {c.title}
                        </span>
                        <span className="mt-0.5 block truncate font-mono text-[10px] uppercase tracking-wider text-ink-faint">
                          {relativeTime(c.updatedAt, now)}
                        </span>
                      </span>
                    </button>

                    {confirming ? (
                      // Light inline confirm — no blocking dialog.
                      <span className="absolute right-1.5 top-1.5 flex items-center gap-0.5">
                        <button
                          type="button"
                          onClick={() => {
                            setConfirmId(null);
                            onDelete(c.id);
                          }}
                          aria-label="Confirmar eliminación"
                          className="grid h-7 w-7 place-items-center rounded-lg bg-tone-red-bg text-tone-red transition-colors hover:bg-tone-red/20"
                        >
                          <Check className="h-4 w-4" strokeWidth={2.25} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmId(null)}
                          aria-label="Cancelar"
                          className="grid h-7 w-7 place-items-center rounded-lg text-ink-soft transition-colors hover:bg-canvas"
                        >
                          <X className="h-4 w-4" strokeWidth={2.25} />
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setConfirmId(c.id)}
                        aria-label={`Eliminar conversación: ${c.title}`}
                        className="absolute right-2 top-2 grid h-7 w-7 place-items-center rounded-lg text-ink-faint opacity-0 transition-opacity hover:bg-canvas hover:text-tone-red focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 group-hover:opacity-100"
                      >
                        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
